import { gqlClient } from '@/api/graphql-client';
import { solarDesignerStateKey } from '@/api/queries/project/useSolarDesignerState';
import { solarDesignDraftsKey } from '@/api/queries/project/useSolarDesignDrafts';
import { graphql } from '@/gql';
import {
  type SaveSolarDesignDraftInput,
  type SolarDesignerStateQuery,
} from '@/gql/graphql';
import { hashKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { applySavedSolarDesignDraft } from './solarDesignDraftCache';

const SaveSolarDesignDraft = graphql(`
  mutation SaveSolarDesignDraft(
    $projectId: ID!
    $input: SaveSolarDesignDraftInput!
  ) {
    saveSolarDesignDraft(projectId: $projectId, input: $input) {
      id
      name
      description
      revision
      updatedAt
      state
    }
  }
`);

export const useSaveSolarDesignDraft = (
  projectId: string,
  queryProjectId: string,
  designId?: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    // Autosave failures stay inline in the designer with a retry action. A
    // global toast on every subsequent edit would repeat the same error.
    meta: { skipGlobalErrorToast: true },
    mutationFn: (input: SaveSolarDesignDraftInput) =>
      gqlClient.request(SaveSolarDesignDraft, { projectId, input }),
    onSuccess: (data, input) => {
      const ownKey = solarDesignerStateKey(queryProjectId, designId);
      const held =
        queryClient.getQueryData<SolarDesignerStateQuery>(ownKey)?.projectById
          .solarDesignDraft?.id === data.saveSolarDesignDraft.id;
      queryClient.setQueryData<SolarDesignerStateQuery>(ownKey, (current) =>
        applySavedSolarDesignDraft(current, data.saveSolarDesignDraft, input)
      );
      // The designer's own entry already holds the saved revision; refetching
      // it after every autosave re-rendered the whole designer. It is
      // refetched only when it did not hold this draft yet (a first save).
      // Other entries for the project (another design, the default draft)
      // are still marked stale.
      const ownHash = hashKey(ownKey);
      void queryClient.invalidateQueries({
        queryKey: solarDesignerStateKey(queryProjectId),
        predicate: (query) => !held || hashKey(query.queryKey) !== ownHash,
      });
      void queryClient.invalidateQueries({
        queryKey: solarDesignDraftsKey(queryProjectId),
      });
    },
  });
};
